// ABOUTME: SPEC-82 D-10 — the revealed control row, and the ONE function that turns a footer's own props
// ABOUTME: (keyboard / mic / stop / camera handlers) into its buttons, so no handler is re-wired anywhere.

import React, { useEffect, useState } from 'react';
import { Camera, Square } from 'lucide-react';
import { u } from '../../ReelChrome/railGeometry';
import { mu } from '../../ReelChrome/mobileGeometry';
import { ARIA } from '../../../uiExperiment/copy';
import { GLYPH, ROW_BOX, REVEAL } from './voiceDockTokens';

/**
 * Build the row's buttons from the footer's props. Labels and glyphs are the footers' own (copied once,
 * here), the handlers are the footers' own (passed through untouched).
 * @returns {Array<{id:string,label:string,glyph?:object,icon?:string,dimmed:boolean,disabled:boolean,onClick:Function}>}
 */
export function buildDockControls({
  isKeyboardOpen, onOpenKeyboard, onCloseKeyboard,
  isMicOn, onToggleMic,
  showStop = false, canStop = false, onStopAgent,
  showCamera = false, onCamera,
}) {
  const controls = [
    {
      id: 'keyboard', label: isKeyboardOpen ? ARIA.hideTextInput : ARIA.typeMessage, glyph: GLYPH.keyboard,
      dimmed: !isKeyboardOpen, disabled: false, onClick: isKeyboardOpen ? onCloseKeyboard : onOpenKeyboard,
    },
    {
      id: 'mic', label: isMicOn ? ARIA.muteMic : ARIA.unmuteMic, glyph: isMicOn ? GLYPH.mic : GLYPH.micOff,
      dimmed: !isMicOn, disabled: false, onClick: onToggleMic,
    },
  ];
  if (showStop) {
    controls.push({ id: 'stop', label: ARIA.stopAgent, icon: 'stop', dimmed: !canStop, disabled: !canStop, onClick: onStopAgent });
  }
  if (showCamera) {
    controls.push({ id: 'camera', label: ARIA.sharePhoto, icon: 'camera', dimmed: false, disabled: false, onClick: onCamera });
  }
  return controls;
}

function DockButton({ control, size, scale }) {
  const { glyph, icon, label, dimmed, disabled, onClick, id } = control;
  return (
    <button
      type="button"
      data-vf={`control-${id}`}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`reel-touch-target relative flex items-center justify-center shrink-0 bg-reel-chrome-panel backdrop-blur-reel-chrome rounded-reel-iconButton hover:bg-white/10 transition-colors ${dimmed ? 'opacity-40' : 'opacity-100'}`}
      style={{ width: scale(size.w), height: scale(size.h) }}
    >
      {glyph && <img src={glyph.src} alt="" aria-hidden="true" style={{ width: scale(glyph.w), height: scale(glyph.h) }} />}
      {icon === 'stop' && <Square size={16} fill="currentColor" className="text-reel-chrome-textPrimary" />}
      {icon === 'camera' && <Camera size={16} className="text-reel-chrome-textPrimary" />}
    </button>
  );
}

/**
 * Mount while open or tweening out, show one tick after mount so the enter transition has a "from".
 * Under reduced motion there is no tween: mounted === shown === open, so nothing animates or lingers.
 */
function useRowPhase(open, reducedMotion) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (reducedMotion) { setMounted(open); setShown(open); return undefined; }
    if (open) {
      setMounted(true);
      const t = setTimeout(() => setShown(true), 16);
      return () => clearTimeout(t);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), REVEAL.ms);
    return () => clearTimeout(t);
  }, [open, reducedMotion]);
  return { mounted: mounted || open, shown: shown && open };
}

// The row's minimum centre-to-centre pitch in real px, so two 44px touch targets never overlap.
const TOUCH_PITCH_PX = 46;

/** The row itself: laid out on the chrome's unit, tweened in/out by opacity + lift (instant under reduced motion). */
export default function RevealedControls({ composition, controls, open, reducedMotion }) {
  const { mounted, shown } = useRowPhase(open, reducedMotion);
  if (!mounted) return null;
  const size = ROW_BOX[composition];
  const scale = composition === 'desktop' ? u : mu;
  const tween = reducedMotion ? 'none' : `opacity ${REVEAL.ms}ms ${REVEAL.easing}, transform ${REVEAL.ms}ms ${REVEAL.easing}`;
  return (
    <div
      data-vf="row"
      role="group"
      aria-label="Controls"
      className="flex items-center"
      style={{
        // Pitch never under 46px (44 + 2: hit-testing is not exact to the pixel at a ::after edge), so neighbouring touch targets
        // tile instead of covering each other on a small handset
        // (the box stays authored, so the lift arithmetic in CONTROLS_LIFT is untouched).
        gap: `max(${scale(size.gap)}, calc(${TOUCH_PITCH_PX}px - ${scale(size.w)}))`,
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : `translateY(${scale(size.h / 2)})`,
        pointerEvents: open ? 'auto' : 'none',
        transition: tween,
      }}
    >
      {controls.map((control) => <DockButton key={control.id} control={control} size={size} scale={scale} />)}
    </div>
  );
}
